import { MEDIA_CACHE_DISABLED, MEDIA_FRAME_CACHE_NAME } from '../config';
import * as cacheApi from './cacheApi';

// Session cache of the last rendered frame of animated media (message GIFs/videos, animated
// stickers), keyed by a prefixed media id. The backend sends no poster/thumbnail for these yet, so
// a re-mounted player shows a black/blank box while it re-initializes — the cached frame fills that
// gap with the exact frame the user already saw. WebP keeps sticker alpha; browsers that cannot
// encode WebP fall back to PNG per the `toDataURL` contract.
//
// The map is mirrored to Cache Storage (disk) so the frames also survive a full reload (F5): RAM is
// wiped on reload, but hydrating this map at boot lets each player paint its last frame immediately
// while the (worker-side) decode warms up again — the same anti-flash trick used when switching chats,
// backed by disk instead of RAM. Re-download is already avoided by `mediaLoader`'s own blob cache;
// this only removes the visual decode flash.
const MAX_ENTRIES = 120;
const CAPTURE_QUALITY = 0.75;
const DISK_KEY = 'frames-v2';
// Frames captured before `MAX_FRAME_SIZE` existed are stored at full resolution, and nothing would
// replace them until 120 newer ones had pushed them out one by one — so the oversized blob (4.9 MB
// when measured) would linger for weeks. Drop that key once instead of migrating it: these are
// throwaway anti-flash placeholders, and every one of them is re-captured the next time its media
// renders.
const LEGACY_DISK_KEY = 'frames';
// Scrolling past animated media captures frames continuously, and each flush re-serializes the WHOLE
// map (up to `MAX_ENTRIES` base64 frames) on the main thread — landing exactly when the thread is
// busiest. The debounce bounds how often that happens; the flushes that matter also fire on
// `visibilitychange`/`pagehide`. It is not longer than this because an OOM kill or a crash — the case
// these placeholders exist to soften — takes everything captured since the last write with it, and
// neither of those events runs an unload handler.
const FLUSH_DEBOUNCE = 5000;
// Longest side of a captured frame. This is a placeholder shown for a few hundred milliseconds while
// the real player warms up, drawn into a chat bubble — capturing at full video resolution stored far
// more detail than it can ever show. Measured before this cap: 119 frames averaging 41 KB, so 4.9 MB
// of base64 re-serialized and written to disk on every debounced flush — which lands while the user
// is scrolling past animated media, exactly when the main thread is busiest. 320px matches the
// thumbnail width the app already requests from the media proxy elsewhere.
const MAX_FRAME_SIZE = 320;

const frameByMediaId = new Map<string, string>();
// On-screen players register a capture thunk keyed by their instance id. Items visible at reload time
// never run their unmount capture (the browser tears the page down without unmount lifecycle), so we
// snapshot them on `visibilitychange`/`pagehide` right before the page goes away.
const capturers = new Map<string, () => void>();

let isDirty = false;
let flushTimeout: ReturnType<typeof setTimeout> | undefined;
let isHydrated = false;
let isSetUp = false;

export function getCachedFirstFrame(mediaId: string | undefined): string | undefined {
  return mediaId ? frameByMediaId.get(mediaId) : undefined;
}

export function captureFirstFrame(
  source: HTMLVideoElement | HTMLCanvasElement, mediaId: string, shouldOverwrite = false,
): void {
  if (!shouldOverwrite && frameByMediaId.has(mediaId)) {
    return;
  }

  try {
    const width = source instanceof HTMLVideoElement ? source.videoWidth : source.width;
    const height = source instanceof HTMLVideoElement ? source.videoHeight : source.height;
    if (!width || !height) return;

    setFrame(mediaId, toScaledDataUrl([source], width, height));
  } catch {
    // Tainted canvas (cross-origin source) or capture failure — the existing preview paths apply
  }
}

// Flatten several stacked canvases (drawn back-to-front) into one cached frame. Composites like the
// slot-machine dice paint through layered `AnimatedSticker` canvases instead of a single `StickerView`,
// so there is no lone source to snapshot — merge the visible layers into one poster for the re-mount.
export function captureCompositeFrame(
  sources: HTMLCanvasElement[], mediaId: string, shouldOverwrite = false,
): void {
  if (!shouldOverwrite && frameByMediaId.has(mediaId)) {
    return;
  }

  const layers = sources.filter((source) => source.width && source.height);
  if (!layers.length) return;

  try {
    const width = Math.max(...layers.map((layer) => layer.width));
    const height = Math.max(...layers.map((layer) => layer.height));

    setFrame(mediaId, toScaledDataUrl(layers, width, height));
  } catch {
    // Tainted canvas (cross-origin source) or capture failure — the existing preview paths apply
  }
}

// Register/unregister an on-screen player's capture thunk so it can be snapshotted right before the
// page is hidden or reloaded. Keyed by a stable per-instance id (not the media id — several messages
// can show the same media).
export function registerFrameCapturer(instanceId: string, capture: () => void): void {
  capturers.set(instanceId, capture);
}

export function unregisterFrameCapturer(instanceId: string): void {
  capturers.delete(instanceId);
}

// Hydrate the RAM map from disk and attach the hide/unload snapshot. Call once at boot, before the
// message list renders, so the first paint can already use the persisted frames.
export function setupMediaFrameCache(): void {
  if (isSetUp || MEDIA_CACHE_DISABLED) return;
  isSetUp = true;

  void hydrateFromDisk();

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      captureVisible();
      void flushToDisk();
    }
  });
  window.addEventListener('pagehide', () => {
    captureVisible();
    void flushToDisk();
  });
}

// Draw the sources back-to-front into one canvas scaled to `MAX_FRAME_SIZE`, and encode that. The
// scaling happens during the draw rather than as a second pass, so a composite costs the same as a
// single layer. Sources already within the cap are drawn at their own size.
function toScaledDataUrl(
  sources: (HTMLCanvasElement | HTMLVideoElement)[], width: number, height: number,
): string {
  const scale = Math.min(1, MAX_FRAME_SIZE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));

  const ctx = canvas.getContext('2d')!;
  sources.forEach((source) => ctx.drawImage(source, 0, 0, canvas.width, canvas.height));

  return canvas.toDataURL('image/webp', CAPTURE_QUALITY);
}

function setFrame(mediaId: string, dataUrl: string): void {
  if (frameByMediaId.size >= MAX_ENTRIES && !frameByMediaId.has(mediaId)) {
    frameByMediaId.delete(frameByMediaId.keys().next().value!);
  }
  frameByMediaId.set(mediaId, dataUrl);
  scheduleFlush();
}

function captureVisible(): void {
  capturers.forEach((capture) => {
    try {
      capture();
    } catch {
      // A player mid-teardown may throw on capture — skip it, the rest still persist
    }
  });
}

function scheduleFlush(): void {
  isDirty = true;
  if (flushTimeout) return;
  flushTimeout = setTimeout(() => {
    flushTimeout = undefined;
    void flushToDisk();
  }, FLUSH_DEBOUNCE);
}

async function flushToDisk(): Promise<void> {
  if (!isDirty || MEDIA_CACHE_DISABLED) return;
  isDirty = false;

  await cacheApi.save(MEDIA_FRAME_CACHE_NAME, DISK_KEY, Object.fromEntries(frameByMediaId));
}

async function hydrateFromDisk(): Promise<void> {
  if (isHydrated) return;
  isHydrated = true;

  void cacheApi.remove(MEDIA_FRAME_CACHE_NAME, LEGACY_DISK_KEY);

  const stored = await cacheApi.fetch(MEDIA_FRAME_CACHE_NAME, DISK_KEY, cacheApi.Type.Json) as
    Record<string, string> | undefined;
  if (!stored) return;

  Object.entries(stored).forEach(([mediaId, dataUrl]) => {
    // Frames captured while hydration was in flight are fresher — never clobber them.
    if (typeof dataUrl === 'string' && !frameByMediaId.has(mediaId)) {
      frameByMediaId.set(mediaId, dataUrl);
    }
  });

  while (frameByMediaId.size > MAX_ENTRIES) {
    frameByMediaId.delete(frameByMediaId.keys().next().value!);
  }
}
